import { formatMoney, type FunnelPhaseDto } from '@nsb/core';
import { dateWords } from '../days.ts';

const monthYear = (d: string) => dateWords(d.slice(0, 10), 'monthYear');

/**
 * A phase's facts, behind the question mark beside its name on the funnel (#129; Jason, 2026-10-29: "all of this info
 * should be hidden as help icons next to the funnel time line zones ... help can say what it is and the approximate
 * uncertainty of the price during this phase"): what it is, when, and how sure the price is by its end. Only what
 * someone has dated has a date, one placed by its share of the time says so; a phase ahead says only how much of the
 * spread the tree leaves open by its end, never a figure nobody set (design study 17, choice 4).
 */
export function phaseHelp(p: FunnelPhaseDto, o: { ended: boolean; priced: boolean }): { title: string; what: string; when: string; sure: string } {
  return {
    title: p.state === 'now' ? `${p.name} · now` : p.state === 'done' ? `${p.name} · done` : p.name,
    what: p.what,
    when: when(p, o.ended),
    sure: sure(p, o),
  };
}

function when(p: FunnelPhaseDto, ended: boolean): string {
  if (p.state === 'done') return p.startOn && p.endOn ? `${monthYear(p.startOn)} – ${monthYear(p.endOn)}` : p.endOn ? `Ended ${monthYear(p.endOn)}` : 'Done';
  if (!p.endOn) return p.state === 'now' && p.startOn ? `Since ${monthYear(p.startOn)}; its end is not dated yet` : ended ? 'Not reached' : 'Not dated yet';
  // Its gate placed by the shares: drawn there, never called dated.
  if (p.placed) return p.state === 'now' && p.startOn ? `Since ${monthYear(p.startOn)}; its end is not dated: placed by its share of the time` : 'Its end is not dated: placed by its share of the time';
  return p.state === 'now' && p.startOn ? `${monthYear(p.startOn)} – about ${monthYear(p.endOn)}` : `Ends about ${monthYear(p.endOn)}`;
}

function sure(p: FunnelPhaseDto, o: { ended: boolean; priced: boolean }): string {
  if (p.state === 'done' && p.range)
    return `The price by its end: ${p.range.lowCents === p.range.highCents ? formatMoney(p.range.lowCents) : `${formatMoney(p.range.lowCents)} to ${formatMoney(p.range.highCents)}`}.`;
  if (p.state !== 'done' && o.priced && !o.ended) return `How sure the price is: by the end of ${p.name}, about ${Math.round(p.leaves * 100)}% of the price’s spread at the start is still open.`;
  return 'No price yet.';
}
